const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price / 100)
}

const findProductInBundles = (variantId) => {
  let foundProduct = null
  let bundleIndex = 1

  while (window[`accordionCarouselProducts${bundleIndex}`]) {
    foundProduct = window[`accordionCarouselProducts${bundleIndex}`].find(p =>
      p.initVariant?.id === parseInt(variantId) ||
      p.variants?.some(v => v.id === parseInt(variantId))
    )
    if (foundProduct) break
    bundleIndex++
  }

  return foundProduct
}

// Mirrors MIN_BUNDLE_PRODUCT_TOTAL in the discount function -- the group has to reach this many
// units before anything is discounted. The main product's own quantity counts towards it, so
// bumping it to 2 qualifies a bundle even with nothing else selected.
export const MIN_BUNDLE_PRODUCT_TOTAL = 2

/**
 * Recomputes the bundle summary price and compare-at price from the main product plus every
 * selected bundle line item, and writes them into the `[data-regular-price]` / `[data-compare-price]`
 * summary elements. Shared by product-bundles.js (accordion state) and plp-card.js (add/remove line
 * items), which both mutate the same `.product-bundles` DOM and must agree on the total.
 *
 * The discount is applied per line -- each item pays the lower of its own sale price or the bundle
 * rate off its own RRP -- because that is what the discount function does at checkout. Quoting a
 * price here that the function will not honour is worse than quoting none.
 */
export const updateBundleTotal = () => {
  const bundleLineItems = document.querySelectorAll('.product-bundles__line-items .product-bundles__line-item')

  const lines = []

  bundleLineItems.forEach(item => {
    const variantId = item.getAttribute('data-variant-id')
    const quantity = parseInt(item.getAttribute('data-quantity') || 1)
    if (variantId) {
      const foundProduct = findProductInBundles(variantId)

      if (foundProduct) {
        const itemPrice = foundProduct.price || 0
        lines.push({
          price: itemPrice,
          // product-json.liquid emits `compareAtPriceMin`, not `compare_at_price_min`.
          comparePrice: foundProduct.compareAtPriceMin || itemPrice,
          quantity
        })
      } else {
        // A line whose product isn't in any accordion's carousel data (e.g. the current PDP
        // product when it isn't itself part of accordion 1's configured collection) falls back
        // to the price snapshotted on the line item when it was added.
        const fallbackPrice = parseFloat(item.getAttribute('data-fallback-price') || 0)
        const fallbackComparePrice = parseFloat(item.getAttribute('data-fallback-compare-price') || fallbackPrice)
        lines.push({
          price: fallbackPrice,
          comparePrice: fallbackComparePrice || fallbackPrice,
          quantity
        })
      }
    }
  })

  const bundleContainer = document.querySelector('.product-bundles')
  const discountPercentage = bundleContainer ? parseFloat(bundleContainer.getAttribute('data-bundle-discount') || 0) : 0

  const totalQuantity = lines.reduce((sum, line) => sum + line.quantity, 0)
  const totalComparePrice = lines.reduce((sum, line) => sum + line.comparePrice * line.quantity, 0)
  const qualifies = totalQuantity >= MIN_BUNDLE_PRODUCT_TOTAL && discountPercentage > 0

  const finalPrice = lines.reduce((sum, line) => {
    const linePrice = line.price * line.quantity
    if (!qualifies) return sum + linePrice
    // A line already discounted past the bundle rate keeps its sale price.
    const bundlePrice = line.comparePrice * line.quantity * (1 - discountPercentage / 100)
    return sum + Math.min(linePrice, bundlePrice)
  }, 0)

  const finalComparePrice = totalComparePrice

  const regularPriceElement = document.querySelector('[data-regular-price]')
  const comparePriceElement = document.querySelector('[data-compare-price]')
  const saveNudgeElement = document.querySelector('[data-bundle-save-nudge]')

  if (regularPriceElement) {
    regularPriceElement.textContent = formatPrice(finalPrice)
  }
  if (comparePriceElement) {
    // Nothing to strike through when the bundle saves nothing.
    comparePriceElement.innerHTML = finalPrice === finalComparePrice
      ? '<span class="product-bundles__original-price"></span>'
      : `<span class="product-bundles__original-price">${formatPrice(finalComparePrice)}</span>`
  }
  // Once the bundle qualifies, the discounted price + strikethrough RRP already show the real
  // saving -- re-quoting a flat percentage here would reintroduce the inaccuracy this nudge avoids.
  if (saveNudgeElement) {
    saveNudgeElement.style.display = qualifies ? 'none' : ''
  }
}
